import { forwardRef, PropsWithRef, useMemo } from 'react';
import { TextField as MUITextField, TextFieldProps } from '@mui/material';
import { EventType } from '@/lib/types';
import {
  compactFilledBorderedInputFixedBoxSx,
  compactFilledFloatingLabelSx,
  compactFilledInputValueSx,
  defaultFilledFloatingLabelSx,
  defaultFilledInputValueSx,
} from '@/lib/compactFilledFieldStyles';
import { Skeleton } from '../Skeleton';
import debounce from 'lodash/debounce';

const DEBOUNCE_DELAY = 500;

export type CustomTextFieldProps = PropsWithRef<TextFieldProps> & {
  loading?: boolean;
  trackChange?: (event: any) => void;
  trackFocus?: (event: any) => void;
  trackEventName?: string;
  eventType?: EventType;
};

export const TextField = forwardRef<HTMLDivElement, CustomTextFieldProps>(
  function TextField(
    {
      loading = false,
      trackChange,
      trackFocus,
      trackEventName,
      eventType = EventType.INPUT_CHANGE,
      ...props
    },
    ref,
  ) {
    // Debounce function to prevent excessive logging
    const debouncedTrackChange = useMemo(() => {
      return debounce((value: string) => {
        if (trackChange && trackEventName) {
          trackChange({
            name: trackEventName,
            properties: {
              eventType,
              fieldName: trackEventName,
              fieldValue: value,
            },
          });
        }
      }, DEBOUNCE_DELAY);
    }, [trackChange, trackEventName, eventType]);

    if (loading) {
      return <Skeleton variant="text" />;
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value ?? '';

      // Use debounced function for tracking
      debouncedTrackChange(value);

      if (props.onChange) {
        props.onChange(event);
      }
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      if (trackFocus && trackEventName) {
        trackFocus({
          name: trackEventName,
          properties: {
            eventType: EventType.INPUT_FOCUS,
            fieldName: trackEventName,
          },
        });
      }
      if (props.onFocus) {
        props.onFocus(event);
      }
    };

    if (props.variant === 'filled') {
      const isSmall = props.size === 'small';
      const filledLabelSx = isSmall
        ? compactFilledFloatingLabelSx
        : defaultFilledFloatingLabelSx;
      const filledInputTypographySx = isSmall
        ? compactFilledInputValueSx
        : defaultFilledInputValueSx;

      return (
        <MUITextField
          {...props}
          ref={ref}
          margin={isSmall ? 'none' : props.margin}
          required={props.required}
          error={props.disabled ? false : props.error}
          onChange={handleChange}
          onFocus={handleFocus}
          InputLabelProps={{
            shrink: true,
            ...props.InputLabelProps,
            sx: {
              ...filledLabelSx,
              ...props.InputLabelProps?.sx,
            },
          }}
          InputProps={{
            disableUnderline: true,
            ...props.InputProps,
            sx: {
              ...filledInputTypographySx,
              ...(isSmall ? compactFilledBorderedInputFixedBoxSx : {}),
              backgroundColor: 'background.paper',
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'divider',
              ':focus-within': {
                borderColor: 'primary.main',
              },
              ':has(input[aria-invalid="true"])': {
                borderColor: 'error.main',
              },
              '&.MuiFilledInput-root': {
                backgroundColor: 'transparent',
              },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                fontStyle: 'italic',
                '& input': {
                  bgcolor: 'transparent',
                  WebkitTextFillColor: 'currentColor',
                },
              },
              ...props.InputProps?.sx,
            },
          }}
          sx={{
            overflow: 'hidden',
            borderRadius: '8px',
            ...props.sx,
          }}
        />
      );
    }

    return (
      <MUITextField
        {...props}
        ref={ref}
        onChange={handleChange}
        onFocus={handleFocus}
      />
    );
  },
);
